import { useMemo, useState } from 'react'
import CLUB from '../../config/club.js'
import { buildGroups } from './ggData.js'
import { SAMPLE_TOURNAMENT } from './sampleTournament.js'

// G-CartSigns — feature #1 bundle: per-group Cart Signs + Printed Scorecards.
// READ-ONLY and self-contained: composes everything from the in-code
// SAMPLE_TOURNAMENT (no DB, no real tournaments). Printing reuses the app's
// window.print() pattern with a scoped print <style> (namespaced .g-print-*)
// so global CSS is untouched — one full-page portrait sheet per group.
// `tournament` defaults to the sandbox sample (so the G-CartSigns sandbox is
// unchanged); the graduated per-tournament "Cart Signs" tab passes the REAL
// tournament. READ-ONLY either way — it only reads + prints.
export default function GCartSigns({ tournament = SAMPLE_TOURNAMENT } = {}) {
  const [mode, setMode] = useState('signs') // 'signs' | 'cards'
  const { meta, groups } = useMemo(() => buildGroups(tournament), [tournament])

  return (
    <div className="card">
      <style>{PRINT_CSS}</style>
      {/* Screen controls (hidden when printing) */}
      <div className="g-screen flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
          {[{ k: 'signs', l: 'Cart Signs' }, { k: 'cards', l: 'Scorecards' }].map((o) => {
            const on = mode === o.k
            return (
              <button key={o.k} onClick={() => setMode(o.k)}
                className="rounded-md px-3 py-1 text-sm font-bold uppercase tracking-wider transition"
                style={on ? { backgroundColor: '#123D26', color: '#fff' } : { backgroundColor: 'transparent', color: '#374151' }}>
                {o.l}
              </button>
            )
          })}
        </div>
        <div className="text-sm text-gray-500">{groups.length} group{groups.length === 1 ? '' : 's'}</div>
        <button className="btn-primary" onClick={() => window.print()} disabled={groups.length === 0}>🖨 Print all</button>
      </div>

      {groups.length === 0 ? (
        <p className="g-screen text-sm text-gray-500 italic py-6 text-center">No teams/flights yet — nothing to print.</p>
      ) : (
        <div className="g-print">
          {groups.map((g) => (
            <div key={g.teamId} className="g-print-page">
              {mode === 'signs' ? <CartSign group={g} meta={meta} /> : <Scorecard group={g} meta={meta} />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Header({ meta }) {
  return (
    <div className="g-hdr" style={{ borderBottom: `3px solid ${CLUB.accentColor}` }}>
      <div className="g-hdr-top">
        <img src={CLUB.logo} alt={CLUB.name} className="g-logo" />
        <div className="g-club">{meta.clubName}</div>
        <div className="g-sponsor">Sponsor</div>
      </div>
      <div className="g-event">{meta.tournamentName}{meta.date ? ` · ${meta.date}` : ''}</div>
    </div>
  )
}

function CartSign({ group, meta }) {
  const g = group
  const line = (players) => players.map((p) => `${p.name}${Number.isFinite(p.playingHandicap) ? ` (${p.playingHandicap})` : ''}`).join('  ·  ')
  return (
    <div className="g-sign">
      <Header meta={meta} />
      <div className="g-sign-meta">
        <div><span className="g-lbl">Flight</span><span className="g-val">{g.flightName}</span></div>
        <div><span className="g-lbl">Start</span><span className="g-val">Hole {g.startingHole}</span></div>
        <div><span className="g-lbl">Tee Time</span><span className="g-val">{g.teeTime || '—'}</span></div>
      </div>
      <div className="g-sign-body">
        <div className="g-team">
          <div className="g-team-name">{g.teamName}</div>
          <div className="g-team-players">{line(g.players)}</div>
        </div>
        {g.opponent ? (
          <>
            <div className="g-vs">vs</div>
            <div className="g-team">
              <div className="g-team-name">{g.opponent.teamName}</div>
              <div className="g-team-players">{line(g.opponent.players)}</div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

function Scorecard({ group, meta }) {
  const holes = Array.from({ length: 18 }, (_, i) => i)
  const players = group.opponent ? [...group.players, ...group.opponent.players] : group.players
  const cell = (v) => (v == null || v === '' ? '' : v)
  return (
    <div className="g-card">
      <Header meta={meta} />
      <div className="g-card-sub">
        {group.flightName} · {group.teamName}{group.opponent ? ` vs ${group.opponent.teamName}` : ''} · Start Hole {group.startingHole} · {group.teeTime || '—'}
      </div>
      <table className="g-grid">
        <thead>
          <tr>
            <th className="g-h-name">Player (HCP)</th>
            {holes.map((h) => <th key={h}>{h + 1}</th>)}
            <th>Tot</th>
          </tr>
          <tr className="g-row-par">
            <th className="g-h-name">Par</th>
            {holes.map((h) => <th key={h}>{meta.pars[h]}</th>)}
            <th>{meta.pars.reduce((s, x) => s + Number(x || 0), 0)}</th>
          </tr>
          <tr className="g-row-si">
            <th className="g-h-name">S.I.</th>
            {holes.map((h) => <th key={h}>{meta.strokeIndex[h]}</th>)}
            <th />
          </tr>
        </thead>
        <tbody>
          {players.map((p, i) => (
            <tr key={i}>
              <td className="g-h-name">{p.name}{Number.isFinite(p.playingHandicap) ? ` (${p.playingHandicap})` : ''}</td>
              {holes.map((h) => (
                <td key={h} className="g-score">
                  <span className="g-dots">{'•'.repeat(p.strokesByHole[h] || 0)}</span>
                </td>
              ))}
              <td />
            </tr>
          ))}
        </tbody>
      </table>
      <div className="g-card-foot">Dots = handicap strokes received on that hole. Enter gross scores; net = gross − dots.</div>
    </div>
  )
}

const PRINT_CSS = `
  .g-print-page { border: 1px solid #e5e7eb; border-radius: 12px; margin-bottom: 16px; padding: 24px; background: #fff; }
  .g-hdr { padding-bottom: 12px; }
  .g-hdr-top { display: flex; align-items: center; gap: 16px; }
  .g-logo { height: 128px; object-fit: contain; }
  .g-club { flex: 1; text-align: center; font-weight: 800; font-size: 30px; color: ${CLUB.primaryColor}; }
  .g-event { text-align: center; font-size: 22px; font-weight: 700; color: #333; margin-top: 10px; }
  .g-sponsor { width: 120px; height: 60px; border: 1px dashed #bbb; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #aaa; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; }
  .g-sign { text-align: center; }
  .g-sign-meta { display: grid; grid-template-columns: 1fr 1fr 1fr; margin: 22px 0; }
  .g-sign-meta > div { text-align: center; }
  .g-sign-meta .g-lbl { display: block; font-size: 16px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.12em; color: #444; }
  .g-sign-meta .g-val { display: block; font-size: 34px; font-weight: 800; color: ${CLUB.primaryColor}; }
  .g-sign-body { text-align: center; margin: 28px 0; }
  .g-team-name { font-size: 48px; font-weight: 800; letter-spacing: 0.03em; color: ${CLUB.primaryColor}; }
  .g-team-players { font-size: 26px; letter-spacing: 0.02em; color: #333; margin-top: 6px; }
  .g-vs { font-size: 20px; font-weight: 800; color: ${CLUB.accentColor}; margin: 14px 0; }
  .g-card-sub { font-size: 13px; color: #444; margin: 10px 0 12px; font-weight: 600; }
  .g-grid { width: 100%; border-collapse: collapse; font-size: 12px; }
  .g-grid th, .g-grid td { border: 1px solid #ccc; text-align: center; padding: 4px 2px; }
  .g-grid .g-h-name { text-align: left; white-space: nowrap; padding: 4px 8px; min-width: 140px; }
  .g-row-par th, .g-row-si th { background: #f6f6f2; font-weight: 600; color: #555; }
  .g-score { height: 30px; }
  .g-dots { color: ${CLUB.accentColor}; font-size: 14px; letter-spacing: 1px; }
  .g-card-foot { font-size: 11px; color: #888; margin-top: 10px; }
  @media print {
    body * { visibility: hidden !important; }
    .g-print, .g-print * { visibility: visible !important; }
    .g-print { position: absolute; left: 0; top: 0; width: 100%; }
    .g-screen { display: none !important; }
    .g-print-page { border: none; border-radius: 0; margin: 0; padding: 0.3in; page-break-after: always; }
    @page { size: portrait; margin: 0.35in; }
  }
`
